"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Tooltip from "../Tooltip";
import { getColor } from "../utils";

// Slider Component Props
interface SliderProps<T extends { label: string }> {
  values: T[];
  selectedValue: T;
  onChange: (value: T, index: number) => void;
  onChangeComplete: (value: T, index: number) => void;
  disabled?: boolean;
}

// Slider Component
export default function Slider<T extends { label: string }>({
  values,
  selectedValue,
  onChange,
  onChangeComplete,
  disabled = false,
}: SliderProps<T>) {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const didDragRef = useRef(false);

  const selectedIndex = useMemo(() => {
    const index = values.indexOf(selectedValue);
    return index >= 0 ? index : 0; // Fallback to 0 if not found
  }, [values, selectedValue]);

  const getPositionFromIndex = useCallback(
    (index: number) => {
      if (values.length <= 1) return 0;
      return (index / (values.length - 1)) * 100;
    },
    [values.length]
  );

  const getIndexFromPosition = useCallback(
    (position: number) => {
      if (values.length <= 1) return 0;
      const normalizedPosition = Math.max(0, Math.min(100, position));
      return Math.round((normalizedPosition / 100) * (values.length - 1));
    },
    [values.length]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      // Ignore clicks that happen right after dragging to avoid multiple API requests
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }

      if (disabled || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const position = ((event.clientX - rect.left) / rect.width) * 100;
      const newIndex = getIndexFromPosition(position);

      if (newIndex !== selectedIndex) {
        onChange(values[newIndex], newIndex);
        onChangeComplete(values[newIndex], newIndex);
      }
    },
    [disabled, selectedIndex, values, onChange, onChangeComplete, getIndexFromPosition]
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) return;
      event.preventDefault();
      didDragRef.current = false; // Reset at start of potential drag
      setIsDragging(true);
    },
    [disabled]
  );

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMoveGlobal = (event: MouseEvent) => {
      if (!sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const position = ((event.clientX - rect.left) / rect.width) * 100;
      const newIndex = getIndexFromPosition(position);

      if (newIndex !== selectedIndex) {
        didDragRef.current = true; // Mark that we actually dragged
        onChange(values[newIndex], newIndex);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      onChangeComplete(values[selectedIndex], selectedIndex);
    };

    document.addEventListener("mousemove", handleMouseMoveGlobal);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMoveGlobal);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, selectedIndex, values, onChange, onChangeComplete, getIndexFromPosition]);

  if (values.length === 0) return null;

  return (
    <SliderContainer
      ref={sliderRef}
      onClick={handleClick}
      style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
      onMouseLeave={handleMouseLeave}>
      <SliderTrack>
        {values.map((value, index) => (
          <React.Fragment key={index}>
            <Tooltip
              direction={Tooltip.Direction.Top}
              source={`slider-tick-${index}`}
              interactive={false}>
              {value.label}
            </Tooltip>
            <SliderTickTarget
              data-tooltip-source={`slider-tick-${index}`}
              position={getPositionFromIndex(index)}
              onClick={e => {
                e.stopPropagation();
                if (index !== selectedIndex) {
                  onChange(values[index], index);
                  onChangeComplete(values[index], index);
                }
              }}
            />
            <SliderTick position={getPositionFromIndex(index)} isActive={index === selectedIndex} />
          </React.Fragment>
        ))}
        <SliderHandle
          position={getPositionFromIndex(selectedIndex)}
          isDragging={isDragging}
          onMouseDown={handleMouseDown}
        />
      </SliderTrack>
    </SliderContainer>
  );
}

// Slider Component Styles
const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background-color: #e5e5e5;
  border-radius: 1px;
`;

const SliderTick = styled.div<{ position: number; isActive: boolean }>`
  position: absolute;
  top: 50%;
  left: ${({ position }) => position}%;
  transform: translate(-50%, -50%);
  width: 1px;
  height: 6px;
  border-radius: 50%;
  background-color: #999;
  z-index: 1;
`;

const SliderTickTarget = styled.div<{ position: number }>`
  position: absolute;
  top: 50%;
  left: ${({ position }) => position}%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  z-index: 3;
  cursor: pointer;
`;

const SliderHandle = styled.div<{ position: number; isDragging: boolean }>`
  position: absolute;
  top: 50%;
  left: ${({ position }) => position}%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 20px;
  background-color: ${props => getColor(props.theme.primaryColor, "primary")};
  border-radius: 2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  cursor: grab;
  transition: left 0.15s ease;
  z-index: 4;

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }

  &:active {
    cursor: grabbing;
  }
`;
